import { RoutingFilterItem } from '../types/RoutingFilterItem';

// Given a flat (ungrouped) list of routing filters spanning multiple domains,
// returns the set of domain names that should render the "missing domain-wide
// Allow rule" warning. A domain is warned when it has >=1 Allow rule
// (isBlocked === false) AND none of those Allow rules are domain-wide
// (projects.length === 0). A domain with zero Allow rules does not warn.
//
// The warning is expressed at the domain grain (not a representative filter id)
// so each row can decide independently whether to show the badge from its own
// domain. This is order-independent and stays correct as filters are added,
// edited, or deleted, and across the flat and grouped views alike.
export function findDomainsMissingDomainWideRule(
  filters: ReadonlyArray<RoutingFilterItem>
): ReadonlySet<string> {
  const byDomain: Record<string, RoutingFilterItem[]> = {};
  for (const filter of filters) {
    if (!byDomain[filter.domain]) {
      byDomain[filter.domain] = [];
    }
    byDomain[filter.domain].push(filter);
  }

  const domainWithWarning = new Set<string>();
  for (const domain of Object.keys(byDomain)) {
    const allow = byDomain[domain].filter(f => !f.isBlocked);
    if (allow.length > 0 && !allow.some(f => f.projects.length === 0)) {
      domainWithWarning.add(domain);
    }
  }
  return domainWithWarning;
}

// The per-filter warning flags surfaced by both the table row badge and the
// edit/reopen dialog. Computed in one place so the two consumers can never
// drift apart on what counts as a warning.
export type RoutingFilterWarnings = Readonly<{
  // The filter is a project-scoped Allow whose domain lacks any domain-wide
  // Allow rule. Requires the domain-level `warnedDomains` set as input because
  // it depends on the other filters in the domain, not just this filter.
  hasMissingDomainWideWarning: boolean;
  // The filter references one or more projects that no longer exist.
  hasDeletedProjects: boolean;
}>;

// Derives the warning flags for a single filter given the set of warned domains
// from findDomainsMissingDomainWideRule. Pure — safe to call from render.
export function getRoutingFilterWarnings(
  filter: RoutingFilterItem,
  warnedDomains: ReadonlySet<string>
): RoutingFilterWarnings {
  return {
    hasMissingDomainWideWarning: !filter.isBlocked && filter.projects.length > 0 && warnedDomains.has(filter.domain),
    hasDeletedProjects: filter.projects.some(p => p.deleted === true),
  };
}
